import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';

const EarlyAccessSignup: React.FC = () => {
  const { t } = useTranslation('landing');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Here you would integrate with your email service (Mailchimp, ConvertKit, etc.)
      console.log('Early access signup:', { email, organization, role });
      
      setStatus('success');
      setMessage(t('earlyAccess.form.success.message'));
      setEmail('');
      setOrganization('');
      setRole('');
    } catch (error) {
      setStatus('error');
      setMessage(t('earlyAccess.form.error'));
    }
  };

  return (
    <section data-signup-section className="py-24 bg-white">
      <div className="container-grid">
        <div className="grid-12">
          <div className="col-span-12 lg:col-span-8 lg:col-start-3 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-display font-bold text-ink-900 mb-6">
                {t('earlyAccess.title')}
              </h2>
              <p className="text-xl text-ink-600 mb-12 max-w-3xl mx-auto">
                {t('earlyAccess.subtitle')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-sand-50 p-8 max-w-2xl mx-auto border-2 border-ink-100"
            >
              {status === 'success' ? (
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-ink-900 mb-2">{t('earlyAccess.form.success.title')}</h3>
                  <p className="text-ink-600">{t('earlyAccess.form.success.message')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="text-center mb-8">
                    <Mail className="w-12 h-12 text-accent-500 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-ink-900 mb-2">{t('earlyAccess.form.title')}</h3>
                    <p className="text-ink-600">{t('earlyAccess.form.subtitle')}</p>
                  </div>

                  <div className="grid gap-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-ink-700 mb-2">
                        {t('earlyAccess.form.email.label')} *
                      </label>
                      <input
                        type="email"
                        id="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-ink-200 focus:border-accent-500 focus:outline-none transition-colors"
                        placeholder={t('earlyAccess.form.email.placeholder')}
                      />
                    </div>

                    <div>
                      <label htmlFor="organization" className="block text-sm font-medium text-ink-700 mb-2">
                        {t('earlyAccess.form.organization.label')}
                      </label>
                      <input
                        type="text"
                        id="organization"
                        value={organization}
                        onChange={(e) => setOrganization(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-ink-200 focus:border-accent-500 focus:outline-none transition-colors"
                        placeholder={t('earlyAccess.form.organization.placeholder')}
                      />
                    </div>

                    <div>
                      <label htmlFor="role" className="block text-sm font-medium text-ink-700 mb-2">
                        {t('earlyAccess.form.role.label')}
                      </label>
                      <select
                        id="role"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-ink-200 focus:border-accent-500 focus:outline-none transition-colors"
                      >
                        <option value="">{t('earlyAccess.form.role.placeholder')}</option>
                        <option value="country-director">{t('earlyAccess.form.role.options.countryDirector')}</option>
                        <option value="program-manager">{t('earlyAccess.form.role.options.programManager')}</option>
                        <option value="field-coordinator">{t('earlyAccess.form.role.options.fieldCoordinator')}</option>
                        <option value="data-analyst">{t('earlyAccess.form.role.options.dataAnalyst')}</option>
                        <option value="emergency-coordinator">{t('earlyAccess.form.role.options.emergencyCoordinator')}</option>
                        <option value="researcher">{t('earlyAccess.form.role.options.researcher')}</option>
                        <option value="other">{t('earlyAccess.form.role.options.other')}</option>
                      </select>
                    </div>
                  </div>

                  {status === 'error' && (
                    <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 border border-red-200">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm">{t('earlyAccess.form.error')}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full bg-accent-500 text-white px-8 py-4 font-medium hover:bg-accent-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status === 'loading' ? t('earlyAccess.form.submitting') : t('earlyAccess.form.submit')}
                  </button>

                  <p className="text-xs text-ink-500 text-center">
                    {t('earlyAccess.form.privacy')}
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EarlyAccessSignup;